import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, AuthType, buscarAlunoPorEmail, buscarProfessorPorEmail } from '../services/api';
import { wasHandledGlobally } from '../services/client';
import { useToast } from '@chakra-ui/react';

interface UserContextProviderProps {
	children: ReactNode;
}

interface UserContextType {
	user?: IUser;
	isLoggedin: boolean;
	setUser: (user: IUser) => void;
	logout: () => void;
	login: (authData: AuthType) => Promise<boolean>;
	functionId: string;
	carregandoSessao: boolean;
}

export interface IUser {
	avatar: string;
	name: string;
	userType: string | undefined;
	username: string;
	sub: string | undefined;
}

export const UserContext = createContext({} as UserContextType);

// Rotas que existem justamente para quem nao tem sessao. Sem sessao o bootstrap
// mandava qualquer URL para /login, entao abrir /forgot-password direto pela
// barra de enderecos, ou recarregar /change-password/:email, jogava a pessoa de
// volta ao login: o codigo recem-recebido por email se perdia e era preciso
// gastar outra das 5 tentativas por minuto para pedir um novo.
const ROTAS_SEM_SESSAO = [ '/login', '/forgot-password', '/change-password' ];

const dispensaSessao = (caminho: string) =>
	ROTAS_SEM_SESSAO.some((rota) => caminho === rota || caminho.startsWith(`${rota}/`));

function AuthContextProvider({ children }: UserContextProviderProps) {
	const [ user, setUser ] = useState<IUser>();
	const [ isLoggedin, setIsLoggedin ] = useState<boolean>(false);
	const [ functionId, setFunctionId ] = useState<string>('');
	// O bootstrap so roda no efeito, depois do primeiro render. Sem esta flag, esse
	// primeiro render acontece com `isLoggedin` falso e as rotas publicas mandam
	// qualquer URL privada para /login, apagando o endereco pedido: quem recarregava
	// /dashboard/alunos caia em /dashboard.
	const [ carregandoSessao, setCarregandoSessao ] = useState<boolean>(true);

	const toast = useToast();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	useEffect(() => {
		// Sessao invalida so precisa tirar a pessoa de onde ela nao pode estar. As
		// rotas publicas ja mandam o resto para /login pelo `path="*"`.
		const encerrarSessao = () => {
			localStorage.removeItem('token');
			localStorage.removeItem('@fId');
			setIsLoggedin(false);
			if (!dispensaSessao(pathname)) navigate('/login');
		};

		async function validUser() {
			const token = localStorage.getItem('token');
			if (token && !user) {
				const decodedToken = decodeToken<IUser>(token);
				try {
					if (decodedToken) {
						setUser(decodedToken);
						setIsLoggedin(true);
						if (decodedToken.username && decodedToken.userType) {
							var fId = localStorage.getItem('@fId');
							if (fId) {
								setFunctionId(fId);
							} else {
								getFunctionId(decodedToken.username, decodedToken.userType);
							}
						} else {
							encerrarSessao();
						}
					} else {
						encerrarSessao();
					}
				} catch (error) {
					console.error(error);
				}
			} else {
				encerrarSessao();
			}
		}
		validUser().finally(() => setCarregandoSessao(false));
		// Bootstrap da sessao: roda uma unica vez ao montar. Incluir `user` faria o efeito
		// rodar de novo apos o setUser, quando `token && !user` ja e falso - caindo no else
		// que apaga o token e redireciona para /login, deslogando quem acabou de entrar.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getFunctionId = (email: string, userType: string): Promise<void> =>
		new Promise((resolve, reject) => {
			if (userType === 'PROFESSOR') {
				buscarProfessorPorEmail(email)
					.then((res) => {
						setFunctionId(res.data.id);
						localStorage.setItem('@fId', res.data.id);
						resolve();
					})
					.catch((err) => {
						navigate('/login');
						reject(err);
						toast({
							title: err,
							status: 'error',
							isClosable: true
						});
					});
			}
			if (userType === 'ALUNO') {
				buscarAlunoPorEmail(email)
					.then((res) => {
						setFunctionId(res.data.id);
						localStorage.setItem('@fId', res.data.id);
						resolve();
					})
					.catch((err) => {
						navigate('/login');
						toast({
							title: err,
							status: 'error',
							isClosable: true
						});
						reject(err);
					});
			}
		});

	const logout = () => {
		localStorage.removeItem('token');
		localStorage.removeItem('@fId');
		navigate('/login');
		setIsLoggedin(false);
	};

	const login = (authData: AuthType): Promise<boolean> =>
		new Promise((resolve, reject) => {
			auth(authData)
				.then(async ({ data }) => {
					localStorage.setItem('token', data.access_token.toString());
					const decoded = decodeToken<IUser>(data.access_token.toString());
					if (decoded) {
						setUser(decoded);
						setIsLoggedin(true);
						if (decoded.userType === 'ALUNO') {
							if (decoded.sub) {
								await getFunctionId(decoded.username, decoded.userType);
								setIsLoggedin(true);
								navigate('/portal');
								resolve(true);
							}
						} else {
							if (decoded.userType === 'PROFESSOR') {
								await getFunctionId(decoded.username, decoded.userType);
								setIsLoggedin(true);
							}
							resolve(true);
							navigate('/dashboard');
						}
					} else {
						localStorage.removeItem('token');
						navigate('/login');
						toast({
							title: `Email ou senha incorretos.`,
							status: 'error',
							isClosable: true
						});
						resolve(false);
					}
				})
				.catch((error) => {
					setIsLoggedin(false);
					if (!wasHandledGlobally(error)) {
						toast({
							title: `Email ou senha incorretos.`,
							status: 'error',
							isClosable: true
						});
					}
					resolve(false);
				});
		});

	return (
		<UserContext.Provider value={{ user, setUser, logout, isLoggedin, login, functionId, carregandoSessao }}>
			{children}
		</UserContext.Provider>
	);
}

export default AuthContextProvider;
